/* ============================================================================
 * fileRequestsPublicReducer.js
 * ============================================================================
 * Reducer del estado de la página pública de file requests (/r/:id).
 *
 * Gestiona:
 * - profile del request (datos visuales/estables · persistido)
 * - session de submission actual (submission_id, server, limits · NO persistido)
 * - errorRequest de previews ante OTP / limit_reached (NO persistido)
 * - infoError: código de error de /info para enrutado de RESULT (NO persistido)
 * - senderDraft: borrador del formulario del remitente (persistido)
 * - currentRequestKey: ancla para limpiar drafts al cambiar de request (persistido)
 * - verifiedEmail: email validado por OTP en flujos con email gate (persistido,
 *   ciclo de vida tab-bound · #269)
 * - migraciones de estado persistido
 *
 * Persistencia: sessionStorage (vive durante la pestaña del navegador, se
 * va al cerrarla). Whitelist granular (D7): profile + senderDraft +
 * currentRequestKey + verifiedEmail. session, errorRequest e infoError NO se
 * persisten (volátiles, dependen de la cookie fr_token).
 *
 * Estructura del state: flat (cada key top-level independiente), NO nested
 * como settingsReducer. Cada bucket tiene ciclo de vida propio (D2).
 *
 * @module redux/reducers/fileRequestsPublic
 * @see ../store/index.js - Registro en combineReducers
 * @see ./index.js - Configuración de redux-persist (whitelist + migrate)
 * ========================================================================== */

import { createSlice } from "@reduxjs/toolkit";

/* ============================================================================
 * Constantes
 * ========================================================================== */

/**
 * Forma por defecto del borrador del formulario del remitente.
 * Cada campo es `string` vacío para que los inputs controlados de
 * react-hook-form arranquen sin warnings de "uncontrolled to controlled".
 *
 * @type {{ submitter_name: string, submitter_email: string, message: string }}
 */
const initialSenderDraft = {
  submitter_name: "",
  submitter_email: "",
  message: "",
};

/**
 * Estado inicial del slice. Forma flat — cada key top-level se persiste o no
 * de forma independiente vía whitelist de redux-persist.
 *
 * @type {Object}
 * @property {Object|null} profile - Datos visuales del request (logo, bg,
 *   título, custom_url, access_mode). Persistido — el chrome aparece
 *   instantáneo tras F5 sin flash YDRAY→white-label.
 * @property {Object|null} session - Datos volátiles de la submission actual
 *   (submission_id, server, limits, resumed, files). NO persistido: depende
 *   de la cookie fr_token, se refetch al montar el Layout.
 * @property {Object|null} errorRequest - Request normalizado adjunto a errores
 *   recuperables (email_verification_required, submission_limit_reached).
 *   Usado para mostrar branding del owner en OTP preview. NO persistido.
 * @property {string|null} infoError - Código de error de la última llamada a
 *   /info o validate-otp. Permite que la Page enrute (email_verification_required
 *   → OTP, submission_limit_reached → RESULT full, request_expired → RESULT
 *   expired, etc.) leyendo del slice en lugar de del `.then(result)` del
 *   handler (que viaja por la Promise y solo lo ve el componente que dispara
 *   el fetch). NO persistido.
 * @property {{submitter_name: string, submitter_email: string, message: string}}
 *   senderDraft - Borrador del formulario del remitente. Persistido — sobrevive
 *   a F5 dentro de la misma pestaña (D5).
 * @property {string|null} currentRequestKey - Identificador del request actual
 *   (id o slug). Sirve para que `syncRequestKey` limpie el draft al cambiar
 *   de request. Persistido.
 * @property {string|null} verifiedEmail - Email validado por OTP en flujos con
 *   email gate (hoy `restricted`, mañana cualquier modo que verifique email
 *   previamente · #269). Forma canónica `email.trim().toLowerCase()`, alineada
 *   con el backend. Persistido — sobrevive F5 dentro de la misma pestaña
 *   (mismo ciclo de vida que la cookie de sesión `fr_token`). Si la cookie
 *   muere antes de la pestaña, `/info` devolverá `email_verification_required`
 *   y la Page transitará a VIEW.OTP — el SenderForm no se monta y el valor
 *   stale no es visible. Tras nueva verificación, `setVerifiedEmail` lo
 *   sobrescribe con el email canónico nuevo.
 */
const initialState = {
  profile: null,
  session: null,
  errorRequest: null,
  infoError: null,
  senderDraft: initialSenderDraft,
  currentRequestKey: null,
  verifiedEmail: null,
};

/**
 * Campos del response normalizado (post `buildSuccessData` en los hooks) que
 * pertenecen al "profile" — datos visuales/estables del request, persistibles.
 * El resto cae en "session" (datos volátiles dependientes de la cookie fr_token).
 *
 * Nota histórica: `active_from`/`active_until` figuraron aquí inicialmente,
 * pero `buildPublicInfo` en el backend no los devuelve (las fechas se
 * traducen en `error: request_not_started` / `request_expired` desde
 * `checkDateWindow`). Se eliminaron para no llevar nunca undefined.
 *
 * @type {string[]}
 */
const PROFILE_FIELDS = [
  "id",
  "name",
  "description",
  "access_mode",
  "custom_url",
  "logo_url",
  "background_url",
];

/* ============================================================================
 * Helpers
 * ========================================================================== */

/**
 * Separa el payload normalizado en `{ profile, session }` según
 * `PROFILE_FIELDS`. Los campos listados van a profile; el resto a session.
 * Idempotente — si recibe `null` devuelve `{ profile: null, session: null }`.
 *
 * @param {Object|null} payload - Payload normalizado por buildSuccessData.
 * @returns {{ profile: Object|null, session: Object|null }}
 */
const splitProfileAndSession = (payload) => {
  if (!payload) return { profile: null, session: null };
  const profile = {};
  const session = { ...payload };
  PROFILE_FIELDS.forEach((field) => {
    if (field in payload) {
      profile[field] = payload[field];
      delete session[field];
    }
  });
  return { profile, session };
};

/* ============================================================================
 * Migraciones
 * ========================================================================== */

/**
 * Migraciones del estado persistido. Cada clave representa una versión
 * anterior que debe adaptarse.
 *
 * Versiones:
 * - 1: shape inicial (F16P2).
 * - 2: añade `verifiedEmail: null` al state (#269).
 *
 * @type {Object<number, Function>}
 */
export const migrations = {
  2: (state) => ({ ...state, verifiedEmail: null }),
};

/**
 * Versión actual del estado para persistencia/migraciones.
 *
 * @type {number}
 */
export const currentVersion = 2;

/* ============================================================================
 * Slice
 * ========================================================================== */

/**
 * Slice Redux de la página pública de file requests.
 *
 * @see ../../pages/layouts/file-requests-layout/FileRequestsLayout.jsx
 *   Consumidor que dispatchea setData/setErrorRequest/setInfoError tras /info.
 * @see ../../pages/FileRequestsPublicPage/FileRequestsPublicPage.jsx
 *   Consumidor que dispatchea clearAll/clearSession en retry/submit.
 * @see ../../pages/FileRequestsPublicPage/FileRequestsPublicPageOtp/FileRequestsPublicPageOtp.jsx
 *   Consumidor que dispatchea setData tras validate-otp.
 */
const fileRequestsPublicSlice = createSlice({
  name: "fileRequestsPublic",
  initialState,
  reducers: {
    /**
     * Procesa el response normalizado completo (de /info o validate-otp en
     * éxito) y lo separa en profile + session. El profile se persiste, el
     * session no (whitelist en redux/reducers/index.js).
     *
     * Resetea errorRequest e infoError porque un éxito invalida cualquier
     * error previo del mismo request.
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {Object|null} action.payload - Payload normalizado por
     *   buildSuccessData (id, name, description, ..., submission_id, server,
     *   limits, resumed, files).
     * @returns {void}
     */
    setData: (state, action) => {
      const { profile, session } = splitProfileAndSession(action.payload);
      state.profile = profile;
      state.session = session;
      state.errorRequest = null;
      state.infoError = null;
    },
    /**
     * Guarda el request adjunto a errores recuperables
     * (email_verification_required, submission_limit_reached). Usado para
     * preservar branding del owner durante OTP preview o pantalla de límite
     * alcanzado.
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {Object|null} action.payload - Request normalizado o null.
     * @returns {void}
     */
    setErrorRequest: (state, action) => {
      state.errorRequest = action.payload;
    },
    /**
     * Publica el código de error de /info al slice para que la Page enrute
     * a `resultReason` ("full" / "not_started" / "expired" / "error") o a
     * VIEW.OTP ("email_verification_required").
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {string|null} action.payload - Código de error o null.
     * @returns {void}
     */
    setInfoError: (state, action) => {
      state.infoError = action.payload;
    },
    /**
     * Merge parcial del borrador del formulario. No destructivo — las claves
     * no incluidas en el payload se conservan. Dispatcheado con debounce de
     * 300ms por SenderForm en cada cambio del usuario (D8) para evitar
     * escrituras síncronas a sessionStorage por keystroke.
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {Partial<{submitter_name:string,submitter_email:string,message:string}>}
     *   action.payload - Campos a sobrescribir.
     * @returns {void}
     */
    setSenderDraft: (state, action) => {
      state.senderDraft = { ...state.senderDraft, ...action.payload };
    },
    /**
     * Si el request actual difiere del guardado, limpia el draft + el email
     * verificado + el branding persistido (profile/errorRequest) + los datos
     * volátiles (session/infoError), y actualiza la key. Llamado al montar el
     * Layout/Page para evitar mostrar draft, email verificado o branding de un
     * request anterior. Si la key coincide, es un no-op (preserva draft +
     * verifiedEmail + profile tras F5 dentro del mismo request — el chrome
     * white-label aparece instantáneo sin flash YDRAY→white-label).
     *
     * Saneamiento de branding en la entrada (#283): `profile` está persistido
     * en sessionStorage y solo se sobrescribe en `setData` (éxito de /info).
     * Si el nuevo request no devuelve branding (programada/expirada/error sin
     * `errorRequest`), sin esta limpieza heredaría el logo/fondo del request
     * anterior. Limpiar aquí cubre todos los caminos de llegada (A→B directo,
     * A→home→B).
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {string|null} action.payload - Identificador del request actual
     *   (id o slug).
     * @returns {void}
     */
    syncRequestKey: (state, action) => {
      if (state.currentRequestKey !== action.payload) {
        state.senderDraft = initialSenderDraft;
        state.verifiedEmail = null;
        state.profile = null;
        state.errorRequest = null;
        state.infoError = null;
        state.session = null;
        state.currentRequestKey = action.payload;
      }
    },
    /**
     * Limpia session + errorRequest + infoError + verifiedEmail pero conserva
     * profile + senderDraft + currentRequestKey. Útil ante cookie fr_token
     * expirada (invalid_token en /submit) para que el siguiente F5 no
     * rehidrate submission_id/server muertos ni un email verificado huérfano,
     * pero mantenga el branding visual y el borrador del usuario (mismo
     * request, misma sesión lógica).
     *
     * @param {Object} state - Estado actual del slice.
     * @returns {void}
     */
    clearSession: (state) => {
      state.session = null;
      state.errorRequest = null;
      state.infoError = null;
      state.verifiedEmail = null;
    },
    /**
     * Persiste el email validado por OTP en flujos con email gate (#269).
     * Dispatcheado por `FileRequestsPublicPageOtp` tras `validate-otp` OK,
     * antes del `setData(...)` que transiciona a IDLE. El valor debe estar
     * en forma canónica (`email.trim().toLowerCase()`) — alineada con el
     * backend, que normaliza así el email en verify-email/validate-otp.
     *
     * El form de remitente (`FileRequestsPublicPageContentSenderForm`) lee
     * este campo y, cuando está presente, override el value del campo email
     * + lo marca como readOnly. Persistido en sessionStorage para sobrevivir
     * F5 dentro de la misma pestaña.
     *
     * @param {Object} state - Estado actual del slice.
     * @param {Object} action - Action Redux Toolkit.
     * @param {string|null} action.payload - Email verificado en forma
     *   canónica, o null para limpiar.
     * @returns {void}
     */
    setVerifiedEmail: (state, action) => {
      state.verifiedEmail = action.payload;
    },
    /**
     * Resetea el slice a `initialState`. Usado tras submit exitoso (D6) y en
     * handleRetry para arrancar limpio.
     *
     * @returns {Object} initialState.
     */
    clearAll: () => initialState,
  },
});

/* ============================================================================
 * Exports
 * ========================================================================== */

/** Action creators del slice fileRequestsPublic. */
export const {
  setData,
  setErrorRequest,
  setInfoError,
  setSenderDraft,
  syncRequestKey,
  clearSession,
  clearAll,
  setVerifiedEmail,
} = fileRequestsPublicSlice.actions;

/* ============================================================================
 * Selectores derivados
 * ============================================================================
 * Convención YDRAY (ver `uploaderReducer.js`): solo se exportan selectores
 * con lógica derivada (fallbacks, conteos, transformaciones). El acceso
 * trivial a campos (`state.fileRequestsPublic.profile`) lo hacen los
 * consumidores con `useSelector` inline.
 *
 * Los siguientes selectores encapsulan la regla "branding cae a errorRequest
 * cuando hay OTP preview" — el chrome debe mostrar logo/bg del owner aunque
 * el request todavía no tenga `profile` (caso email_verification_required).
 * ========================================================================== */

/**
 * URL del logo del owner con fallback a errorRequest. Permite que el chrome
 * white-label aparezca incluso en VIEW.OTP, donde solo hay `errorRequest`
 * (no `profile`). Devuelve null si ningún origen aporta logo.
 *
 * @param {Object} state - Estado raíz de Redux.
 * @returns {string|null}
 */
export const selectFileRequestsPublicLogoUrl = (state) =>
  state.fileRequestsPublic.profile?.logo_url ??
  state.fileRequestsPublic.errorRequest?.logo_url ??
  null;

/**
 * URL del background del owner con fallback a errorRequest. Mismo
 * razonamiento que `selectFileRequestsPublicLogoUrl`. El Layout aplica
 * Opción C estricta sobre este valor (descarta bg si no hay logo).
 *
 * @param {Object} state - Estado raíz de Redux.
 * @returns {string|null}
 */
export const selectFileRequestsPublicBackgroundUrl = (state) =>
  state.fileRequestsPublic.profile?.background_url ??
  state.fileRequestsPublic.errorRequest?.background_url ??
  null;

/** Reducer del slice fileRequestsPublic. */
export default fileRequestsPublicSlice.reducer;
